import IntentService from 'src/services/IntentService';
import LeadStreamsService from 'src/services/LeadStreamsService';
import { getStreamDisplayName, getStreamStatus } from 'src/utils/leadStreams';

import type { Workspace, Profile } from 'src/services/IntentService';
import type { Stream } from 'src/services/LeadStreamsService/agent';

export type Flow = { key: string; name: string; workspace?: Workspace; stream?: Stream };
export function flowStatus(flow: Flow) {
    const statuses = flow.stream
        ? [getStreamStatus(flow.stream).key]
        : (flow.workspace?.streams || []).map(
              (stream) => stream.status || (stream.balance > 0 ? (stream.is_active ? 'active' : 'paused') : 'pending')
          );
    const key = !statuses.length
        ? 'setup'
        : ['active', 'paused', 'ended'].find((status) => statuses.includes(status)) || 'pending';
    const labels: Record<string, string> = {
        setup: 'Настройка потока',
        active: 'Поиск работает',
        paused: 'Поиск на паузе',
        ended: 'Лиды закончились',
        pending: 'Нужен пакет',
    };
    return { key, label: labels[key] };
}
export function combineFlows(workspaces: Workspace[], streams: Stream[]): Flow[] {
    const linked = new Set(workspaces.flatMap((w) => w.streams.map((s) => s.id)));
    return [
        ...workspaces.map((workspace) => ({ key: 'flow=' + workspace.id, name: workspace.name, workspace })),
        ...streams
            .filter((s) => !linked.has(s.id))
            .map((stream) => ({ key: 'stream=' + stream.id, name: getStreamDisplayName(stream), stream })),
    ];
}
export const flowUrl = (flow: Flow, view = 'leads') => {
    const streamId = flow.stream?.id || flow.workspace?.streams[0]?.id;
    const hash = ['funnel', 'business', 'knowledge'].includes(view) ? '#' + view : '';
    return streamId
        ? '/leadstream/' + streamId + hash
        : '/leadstream' + (flow.workspace ? '?draft=' + flow.workspace.id : '');
};
export function streamProfile(stream: Stream): Profile {
    const p = stream.filter_profile;
    return {
        description: p?.customer_description || '',
        category_id: stream.category.id,
        region: p?.geo || '',
        criteria: (p?.target_intents || []).join('\n'),
        exclusions: (p?.exclude || []).join('\n'),
    };
}
// Existing server entities remain intact. A stream is attached only after an explicit
// save/connect action, never while listing or viewing customer data.
export async function attachStream(
    stream: Stream,
    draft?: Workspace,
    prepared?: (w: Workspace) => void
): Promise<Workspace> {
    const { workspaces } = await IntentService.list();
    const linked = workspaces.find((w) => w.streams.some((s) => s.id === stream.id));
    if (linked) return linked;
    const created = draft || (await IntentService.create(getStreamDisplayName(stream).slice(0, 120)));
    prepared?.(created);
    const saved = await IntentService.save({ ...created, profile: { ...created.profile, ...streamProfile(stream) } });
    prepared?.(saved);
    await LeadStreamsService.updateStream(stream.id, { project: saved.project_id });
    return {
        ...saved,
        streams: [
            {
                id: stream.id,
                name: getStreamDisplayName(stream),
                category_name: stream.category.name,
                category_id: stream.category.id,
                balance: stream.balance,
                status: getStreamStatus(stream).key,
                is_active: stream.is_active,
                access_expires_at: stream.access_expires_at,
                access_name: stream.active_access_offer_name,
            },
        ],
    };
}
